import { useState } from "react";
import { List, LineChart } from "lucide-react";
import clsx from "clsx";
import { WatchlistPanel } from "@/components/watchlist/WatchlistPanel";
import { TradingPanel } from "@/components/trading/TradingPanel";
import type { TradeRow } from "@/hooks/useTrades";
import type { SymbolEntry } from "@/lib/symbols";

type Tab = "watchlist" | "trading";

interface RightSidebarProps {
  // Watchlist
  currentSymbol: string;
  currentExchange: string;
  onSelectSymbol: (entry: SymbolEntry) => void;
  // Trading (passthrough to TradingPanel)
  currentPrice: number | null;
  autoCloseActive?: boolean;
  onBuy: () => void;
  onSell: () => void;
  onClosePosition: () => void;
  onUpdateSL: (price: number | null) => void;
  onUpdateTP: (price: number | null) => void;
  trades: TradeRow[];
}

export function RightSidebar(props: RightSidebarProps) {
  const [tab, setTab] = useState<Tab>("watchlist");

  return (
    <aside className="flex h-full w-80 shrink-0 flex-col border-l border-border bg-bg-panel">
      {/* Tab bar */}
      <div className="flex shrink-0 border-b border-border">
        <TabButton active={tab === "watchlist"} onClick={() => setTab("watchlist")} icon={List} label="Watchlist" />
        <TabButton active={tab === "trading"}   onClick={() => setTab("trading")}   icon={LineChart} label="Trade" />
      </div>

      {/* Tab content */}
      <div className="relative flex-1 overflow-hidden">
        {tab === "watchlist" ? (
          <WatchlistPanel
            currentSymbol={props.currentSymbol}
            currentExchange={props.currentExchange}
            onSelect={props.onSelectSymbol}
          />
        ) : (
          <div className="h-full overflow-y-auto">
            <TradingPanel
              currentPrice={props.currentPrice}
              autoCloseActive={props.autoCloseActive}
              onBuy={props.onBuy}
              onSell={props.onSell}
              onClosePosition={props.onClosePosition}
              onUpdateSL={props.onUpdateSL}
              onUpdateTP={props.onUpdateTP}
              trades={props.trades}
            />
          </div>
        )}
      </div>
    </aside>
  );
}

function TabButton({
  active, onClick, icon: Icon, label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2 text-xs font-medium transition-colors",
        active
          ? "border-accent text-accent"
          : "border-transparent text-text-secondary hover:text-text-primary"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </button>
  );
}
